/* Müşteri olay bildirimi — /api/CustomerIncident ve /api/CustomerIncidentPool */

export type IncidentType =
  | "NotFoundAtAddressOrDoorUnanswered"
  | "PhoneUnreachable"
  | "WrongOrIncompleteAddress"
  | "RefusedDelivery"
  | "DidNotPayOnDelivery"
  | "CancelledAfterPreparationStarted"
  | "AbuseOrThreat"
  | "Other";

export type IncidentStatus = "PendingConfirmation" | "Confirmed" | "Disputed" | "Rejected" | "Removed";
export type ReporterKind = "Manager" | "Staff" | "Courier";
export type RiskLevel = "None" | "Info" | "Warning" | "High";
export type ChangeRequestKind = "Correct" | "Delete";
export type ChangeRequestStatus = "Open" | "Approved" | "Rejected" | "Withdrawn";
export type ActorKind = "Reporter" | "Manager" | "SuperAdmin" | "System";
export type RevisionAction =
  | "Created" | "Confirmed" | "Rejected" | "ChangeRequested" | "RequestWithdrawn" | "RequestApproved"
  | "RequestRejected" | "Corrected" | "Removed" | "MarkedDisputed" | "Restored" | "EvidenceAdded" | "EvidenceRemoved";

export interface IIncident {
  id: string;
  restourantId: string;
  /** Siparişsiz (rehber) bildirimde gelmez */
  orderId?: string;
  orderCode?: string;
  type: IncidentType;
  status: IncidentStatus;
  description: string;
  occurredAtUtc: string;
  createdAtUtc: string;
  createdByUserId: string;
  createdByKind: ReporterKind;
  reviewedByUserId?: string;
  reviewedAtUtc?: string;
  hasEvidence: boolean;
}

export interface IRiskEntry { type: IncidentType; date: string }

/** Yanıtta hiç yoksa risk HESAPLANAMADI demektir ("bilinmiyor") — level "None" ise temiz. */
export interface ICustomerRisk {
  level: RiskLevel;
  distinctOrderCount: number;
  incidents?: IRiskEntry[];
}

export interface IOrderIncidents {
  customerRisk?: ICustomerRisk;
  ownIncidents: IIncident[];
}

export interface IRestaurantIncidents {
  poolMember: boolean;
  pendingCount: number;
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  items: IIncident[];
}

export interface IEvidence { incident: IIncident; url: string; expiresAtUtc: string }

export interface IRevision {
  atUtc: string;
  actorUserId?: string;
  actorKind: ActorKind;
  action: RevisionAction;
  fromStatus?: IncidentStatus;
  toStatus?: IncidentStatus;
  reason?: string;
  previousType?: IncidentType;
  previousDescription?: string;
}

export interface IChangeRequest {
  id: string;
  incidentId: string;
  kind: ChangeRequestKind;
  status: ChangeRequestStatus;
  reason: string;
  proposedType?: IncidentType;
  proposedDescription?: string;
  requestedByUserId: string;
  requestedAtUtc: string;
  decidedByUserId?: string;
  decidedAtUtc?: string;
  decisionReason?: string;
}

export interface IIncidentHistory {
  incident: IIncident;
  revisions: IRevision[];
  changeRequests: IChangeRequest[];
}

export interface IChangeRequestQueue {
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  items: { request: IChangeRequest; incident: IIncident }[];
}

export interface IPoolMember { restourantId: string; restourantName: string; addedAtUtc: string; addedByUserId: string }
export interface IPool { memberCount: number; minimumMembers: number; isActive: boolean; members: IPoolMember[] }

/* ───────────────────────── Türkçe metinler ───────────────────────── */

export const INCIDENT_TYPES: { value: IncidentType; label: string; icon: string; onlyCancelled?: boolean }[] = [
  { value: "NotFoundAtAddressOrDoorUnanswered", label: "Adreste bulunamadı / kapıyı açmadı", icon: "bi-door-closed" },
  { value: "PhoneUnreachable", label: "Telefonla ulaşılamadı", icon: "bi-telephone-x" },
  { value: "WrongOrIncompleteAddress", label: "Yanlış veya eksik adres", icon: "bi-geo-alt" },
  { value: "RefusedDelivery", label: "Teslim almayı reddetti", icon: "bi-hand-thumbs-down" },
  { value: "DidNotPayOnDelivery", label: "Teslimatta ödeme yapmadı", icon: "bi-cash-coin" },
  { value: "CancelledAfterPreparationStarted", label: "Hazırlık başladıktan sonra iptal", icon: "bi-x-octagon", onlyCancelled: true },
  { value: "AbuseOrThreat", label: "Küfür / hakaret / tehdit", icon: "bi-exclamation-octagon" },
  { value: "Other", label: "Diğer", icon: "bi-three-dots" },
];

export const typeLabel = (t?: string) => INCIDENT_TYPES.find((x) => x.value === t)?.label ?? t ?? "—";
export const typeIcon = (t?: string) => INCIDENT_TYPES.find((x) => x.value === t)?.icon ?? "bi-flag";

export const STATUS_META: Record<IncidentStatus, { label: string; color: string; bg: string; icon: string }> = {
  PendingConfirmation: { label: "Onay bekliyor", color: "#b45309", bg: "#fef3c7", icon: "bi-hourglass-split" },
  Confirmed:           { label: "Onaylı",        color: "#15803d", bg: "#dcfce7", icon: "bi-check-circle-fill" },
  Disputed:            { label: "İtirazda",      color: "#7c3aed", bg: "#ede9fe", icon: "bi-chat-square-quote-fill" },
  Rejected:            { label: "Reddedildi",    color: "#6b7280", bg: "#f3f4f6", icon: "bi-x-circle" },
  Removed:             { label: "Kaldırıldı",    color: "#991b1b", bg: "#fee2e2", icon: "bi-trash3" },
};

export const RISK_META: Record<RiskLevel, { label: string; color: string; bg: string; icon: string }> = {
  None:    { label: "Temiz",          color: "#15803d", bg: "#dcfce7", icon: "bi-shield-check" },
  Info:    { label: "Bilgi",          color: "#1d4ed8", bg: "#dbeafe", icon: "bi-info-circle-fill" },
  Warning: { label: "Uyarı",          color: "#b45309", bg: "#fef3c7", icon: "bi-exclamation-triangle-fill" },
  High:    { label: "Yüksek dikkat",  color: "#b91c1c", bg: "#fee2e2", icon: "bi-exclamation-octagon-fill" },
};

export const REPORTER_LABEL: Record<ReporterKind, string> = { Manager: "Yönetici", Staff: "Personel", Courier: "Kurye" };

export const REQUEST_STATUS_LABEL: Record<ChangeRequestStatus, string> = {
  Open: "Açık", Approved: "Onaylandı", Rejected: "Reddedildi", Withdrawn: "Geri çekildi",
};

export const ACTION_LABEL: Record<RevisionAction, string> = {
  Created: "Oluşturuldu", Confirmed: "Onaylandı", Rejected: "Reddedildi", ChangeRequested: "Değişiklik talep edildi",
  RequestWithdrawn: "Talep geri çekildi", RequestApproved: "Talep onaylandı", RequestRejected: "Talep reddedildi",
  Corrected: "Düzeltildi", Removed: "Kaldırıldı", MarkedDisputed: "İtiraza alındı", Restored: "Geri alındı",
  EvidenceAdded: "Görsel eklendi", EvidenceRemoved: "Görsel silindi",
};

export const ACTOR_LABEL: Record<ActorKind, string> = {
  Reporter: "Bildiren", Manager: "Yönetici", SuperAdmin: "Mutfacık (SuperAdmin)", System: "Sistem",
};

export const DESCRIPTION_MAX = 500;
export const REASON_MAX = 500;
